import React from 'react';

import tellingYourStoryImg from '@backrs_ui/grow-ui/src/assets/images/gpa-guide/telling-your-story.jpg';
import { GuidePrinciplesGrid } from '@backrs_ui/grow-ui/src/components/GuidePage';
import type {
  GpaGuideCorePrinciples,
  GpaGuideTellingYourStory,
} from '@backrs_ui/grow-ui/src/pages/GpaGuide/content/types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type CorePrinciplesSectionProps = Readonly<{
  content: GpaGuideCorePrinciples;
  storyContent: GpaGuideTellingYourStory;
}>;

export const CorePrinciplesSection: React.FC<CorePrinciplesSectionProps> = ({
  content,
  storyContent,
}) => (
  <Box sx={outerSx}>
    <GuidePrinciplesGrid principles={content.principles} />

    <Box sx={storyCardSx}>
      <Box sx={storyImgWrapperSx}>
        <Box
          component="img"
          src={tellingYourStoryImg}
          alt="Student writing in classroom"
          sx={storyImgSx}
        />
      </Box>
      <Box sx={storyTextSx}>
        <Typography variant="text-lg-semibold" sx={storyTitleSx}>
          {storyContent.title}
        </Typography>
        <Typography variant="text-sm-regular" sx={storyIntroSx}>
          {storyContent.intro}
        </Typography>
      </Box>
      <Box sx={storyQuestionsSx}>
        <Box component="ul" sx={storyListSx}>
          {storyContent.questions.map((question) => (
            <Typography
              key={question}
              component="li"
              variant="text-sm-regular"
              sx={storyQuestionItemSx}
            >
              {question}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  </Box>
);

const outerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
});

const storyCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
  overflow: 'hidden',
  '@container (min-width: 600px)': {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
});

const storyImgWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: '200px',
  flexShrink: 0,
  overflow: 'hidden',
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary']}`,
  '@container (min-width: 600px)': {
    width: '220px',
    height: 'auto',
    borderBottom: 'none',
    borderRight: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary']}`,
  },
});

const storyImgSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

const storyTextSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  p: backrs.spacing['spacing-3xl'],
  minWidth: 0,
});

const storyTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const storyIntroSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const storyQuestionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  px: backrs.spacing['spacing-3xl'],
  pb: backrs.spacing['spacing-3xl'],
  minWidth: 0,
  '@container (min-width: 600px)': {
    flex: 1,
    p: backrs.spacing['spacing-3xl'],
  },
});

const storyListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  pl: backrs.spacing['spacing-md'],
  m: 0,
});

const storyQuestionItemSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  '&::marker': {
    color: backrs.colors['Colors/Foreground/fg-brand-primary'],
  },
});
